import { Avatar, Box, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { formatDateShort } from './dates'
import { NoteAdminDto, TagAdminDto, TagRelationAdminDto } from 'service-api/src/types/api'
import { isEmpty } from './common'

export const filterP1PetTags = (tags: TagRelationAdminDto[] = []) =>
  tags.filter(({ tag }) => tag?.type === 'PET' && tag?.priority === 1)

const sortTagsByPriority = (a: TagAdminDto, b: TagAdminDto) => {
  return a.priority === b.priority
    ? (a.displayName || '').localeCompare(b.displayName || '')
    : (a.priority || 0) - (b.priority || 0)
}

const TooltipContent = ({ note }: { note: { updatedAt?: string; noteBody?: string } }) => {
  const { palette } = useTheme()
  return (
    <Stack direction={'column'} sx={{ color: palette.text.primary }} spacing={1}>
      {note.updatedAt && <Typography variant="body2bold">{formatDateShort(new Date(note.updatedAt))}</Typography>}
      <Typography variant="body2">{note.noteBody}</Typography>
    </Stack>
  )
}

type ChipContentProps = {
  tag: TagAdminDto
  onTagClick?: (tagId: string | null) => void
  isSelected?: boolean
  hasSelection?: boolean
  elevated?: boolean
}

const ChipContent = React.forwardRef<HTMLSpanElement, ChipContentProps>(
  ({ tag, onTagClick, isSelected, hasSelection, elevated, ...props }, ref) => {
    const handleClick = onTagClick ? () => onTagClick(isSelected ? null : (tag.id ?? null)) : undefined
    const dimmed = hasSelection && !isSelected
    if (tag.type === 'USER') {
      return (
        <Box
          ref={ref}
          {...props}
          onClick={handleClick}
          sx={{
            backgroundColor: 'action.selected',
            px: 1,
            py: 0.2,
            borderRadius: 2,
            display: 'inline-flex',
            cursor: onTagClick ? 'pointer' : 'default',
            opacity: dimmed ? 0.45 : 1,
            boxShadow: isSelected && hasSelection ? '0px 3px 8px 0px rgba(0,0,0,0.4)' : 'none',
            transition: 'opacity 0.15s ease, box-shadow 0.15s ease',
          }}
        >
          <Typography variant="caption">{tag.displayName}</Typography>
        </Box>
      )
    }
    return (
      <Box ref={ref} {...props} style={{ display: 'inline-flex' }}>
        <Chip
          style={{ maxWidth: '21ch', breakInside: 'avoid' }}
          label={tag?.displayName}
          size={'small'}
          clickable={!!onTagClick}
          onClick={handleClick}
          avatar={
            tag?.icon ? (
              <Avatar
                sx={{
                  backgroundColor: 'transparent',
                  '&.MuiChip-avatar': { p: 0, m: 0, color: tag?.color || 'background.default', paddingLeft: '4px' },
                  '&.MuiChip-label': { paddingX: '4px' },
                }}
              >
                {tag?.icon}
              </Avatar>
            ) : (
              <></>
            )
          }
          sx={{
            borderColor: tag?.color || 'background.default',
            borderWidth: 2,
            opacity: dimmed ? 0.45 : 1,
            boxShadow: isSelected && hasSelection ? '0px 3px 8px 0px rgba(0,0,0,0.4)' : 'none',
            transition: 'opacity 0.15s ease, box-shadow 0.15s ease',
          }}
          variant="outlined"
        />
      </Box>
    )
  }
)
ChipContent.displayName = 'ChipContent'

type SingleTagProps = {
  tag: TagAdminDto
  notes?: NoteAdminDto[]
  onTagClick?: (tagId: string | null) => void
  isSelected?: boolean
  hasSelection?: boolean
  elevated?: boolean
}

const SingleTag = ({ tag, notes, onTagClick, isSelected, hasSelection, elevated }: SingleTagProps) => {
  const { palette } = useTheme()
  const tagNote = React.useMemo(() => {
    const matchingNote = notes?.find((note) => note.tags?.some((r) => r.tagId === tag.id || r.tag?.id === tag.id))
    return matchingNote ? { updatedAt: matchingNote.updatedAt, noteBody: matchingNote.noteBody } : undefined
  }, [tag, notes])

  return tagNote ? (
    <Tooltip
      title={<TooltipContent note={tagNote} />}
      componentsProps={{
        popper: elevated ? undefined : { sx: { zIndex: 1050 } },
        tooltip: {
          sx: {
            border: '3px solid',
            borderColor: palette.secondary.light,
            backgroundColor: palette.background.paper,
            m: 0,
            p: 1,
          },
        },
      }}
    >
      <ChipContent
        tag={tag}
        onTagClick={onTagClick}
        isSelected={isSelected}
        hasSelection={hasSelection}
        elevated={elevated}
      />
    </Tooltip>
  ) : (
    <ChipContent
      tag={tag}
      onTagClick={onTagClick}
      isSelected={isSelected}
      hasSelection={hasSelection}
      elevated={elevated}
    />
  )
}

export const renderTag = (
  tag: TagAdminDto,
  notes?: NoteAdminDto[],
  onTagClick?: (tagId: string | null) => void,
  selectedTagId?: string | null,
  // elevated means its on dashboard
  elevated?: boolean
) => (
  <SingleTag
    key={tag.id}
    tag={tag}
    notes={notes}
    onTagClick={onTagClick}
    isSelected={selectedTagId === tag.id}
    hasSelection={selectedTagId != null}
    elevated={elevated}
  />
)

export const renderTags = (
  tags: TagRelationAdminDto[] = [],
  tagType?: TagAdminDto['type'],
  notes?: NoteAdminDto[],
  onTagClick?: (tagId: string | null) => void,
  selectedTagId?: string | null,
  elevated?: boolean
) => {
  return tags
    .slice()
    .filter((t) => (isEmpty(tagType) ? true : t.tag?.type === tagType))
    .sort((a, b) => sortTagsByPriority(a.tag as TagAdminDto, b.tag as TagAdminDto))
    .map(({ tag }) => renderTag(tag as TagAdminDto, notes, onTagClick, selectedTagId, elevated))
}

export const renderSimpleTags = (tags: TagAdminDto[], notes?: NoteAdminDto[]) => {
  return tags
    .slice()
    .sort(sortTagsByPriority)
    .map((tag) => renderTag(tag, notes))
}

export const renderPetInstructionTags = (tags: TagAdminDto[] = [], notes?: NoteAdminDto[]) => {
  return tags
    .slice()
    .sort((a, b) => sortTagsByPriority(a as TagAdminDto, b as TagAdminDto))
    .map((tag) => renderTag(tag as TagAdminDto, notes))
}
